import { Immutable } from "immer";
import React, { memo, useCallback, useRef, useState } from "react";
import { TransitionGroup } from "react-transition-group";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import * as userAPI from "../../../api/user";
import { CSSTransitionWithRef } from "../../../components/CssTransition";
import { Link } from "../../../components/Link";
import { SVGIcon } from "../../../components/SVGIcon";
import { Form } from "../../../forms/Form";
import { FormInput } from "../../../forms/FormInput";
import { FormPasswordInput } from "../../../forms/FormPasswordInput";
import { FormSubmit } from "../../../forms/FormSubmit";
import { FORM_CLASS_NAME } from "../../../forms/classNames";
import { strings } from "../../../localization";
import { WebPageURL } from "../../../models/nominals";

const BASE_CLASS = "thank-you-create-account";

type IAuthenticatedOrderProps = Immutable<{ orderHistoryURL: WebPageURL }>;

type IAnonOrderProps = Immutable<{
    firstName: string;
    email: string;
    orderHistoryURL: WebPageURL;
}>;

type IAnonNewUserProps = Immutable<{
    email: string;
    orderHistoryURL: WebPageURL;
}>;

type ICreateAccountProps = Immutable<{
    firstName: string;
    email: string;
    isAuthenticatedAsOrderOwner: boolean;
    orderOwnerHasPassword: boolean;
    orderHistoryURL: WebPageURL;
}>;

// ─── Branch 4: Authenticated user placed an order ────────────────────────────
// Stateless — memo prevents re-render unless orderHistoryURL changes.
const AuthenticatedUserOrder = memo(
    (props: IAuthenticatedOrderProps): React.JSX.Element => {
        // Reqs Branch 4: An authenticated user placed an order.
        return (
            <>
                <h2 className={`${BASE_CLASS}__title`}>
                    {t`Thanks for ordering from us again.`}
                </h2>
                <p className={`${BASE_CLASS}__copy`}>
                    {richT(
                        strings.get("ORDER_THANK_YOU_B4_CREATE_ACCT_COPY") ??
                            "",
                        {
                            MyOrdersLink: (children) => (
                                <Link
                                    className={`${BASE_CLASS}__link`}
                                    href={props.orderHistoryURL}
                                    target="_top"
                                >
                                    {children}
                                </Link>
                            ),
                        },
                    )}
                </p>
            </>
        );
    },
);

// ─── Branch 3: Anon order, email already has a registered account ─────────────
// Stateless — memo prevents re-render unless any prop changes.
const AnonOrderForRegisteredUser = memo(
    (props: IAnonOrderProps): React.JSX.Element => {
        // Reqs Branch 3: An anonymous user placed an order using an email address that was
        // already associated with a user account in the Registered status (i.e. order user
        // does have a password).
        return (
            <>
                <h2 className={`${BASE_CLASS}__title`}>
                    {t`Welcome back, ${props.firstName}`}
                </h2>
                <p className={`${BASE_CLASS}__copy`}>
                    {richT(
                        strings.get("ORDER_THANK_YOU_B3_CREATE_ACCT_COPY") ??
                            "",
                        {
                            UserEmail: () => <strong>{props.email}</strong>,
                            LoginLink: (children) => (
                                <Link
                                    className={`${BASE_CLASS}__link`}
                                    href={props.orderHistoryURL}
                                    target="_top"
                                >
                                    {children}
                                </Link>
                            ),
                        },
                    )}
                </p>
            </>
        );
    },
);

// ─── Branch 2: Anon order, email has a non-registered (lazy) account ────────────
// Has its own submit/disabled state so parent never re-renders on interaction.
const AnonOrderForNonRegisteredUser = memo(
    (props: IAnonOrderProps): React.JSX.Element => {
        // Reqs Branch 2: An anonymous user placed an order using an email address that was
        // already associated with a user account in the Non-Registered status (i.e. order
        // user does not have a password).
        const [disableSubmit, setDisableSubmit] = useState(false);
        const [formSubmitted, setFormSubmitted] = useState(false);
        const disableSubmitRef = useRef(false);

        const onSubmit = useCallback(
            async (event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                if (disableSubmitRef.current) {
                    return;
                }
                disableSubmitRef.current = true;
                setDisableSubmit(true);
                try {
                    await userAPI.login(props.email, "", props.orderHistoryURL);
                    setFormSubmitted(true);
                } catch (e) {
                    console.error(e);
                    disableSubmitRef.current = false;
                    setDisableSubmit(false);
                }
            },
            [props.email, props.orderHistoryURL],
        );

        if (formSubmitted) {
            return (
                <>
                    <h2 className={`${BASE_CLASS}__title`}>
                        {t`Welcome back, ${props.firstName}`}
                    </h2>
                    <p className={`${BASE_CLASS}__copy`}>
                        {richT(
                            t`Your account has been successfully created. For security purposes, we have sent an email to <UserEmail /> to allow you to set your password.`,
                            {
                                UserEmail: () => <strong>{props.email}</strong>,
                            },
                        )}
                    </p>
                </>
            );
        }

        return (
            <Form
                className={concatClassNames([
                    FORM_CLASS_NAME,
                    `${BASE_CLASS}__validate-email-form`,
                ])}
                onSubmit={onSubmit}
            >
                <input type="hidden" value={props.email} />
                <FormSubmit
                    className={`button ${BASE_CLASS}__submit`}
                    value={t`Create Account`}
                    disabled={disableSubmit}
                />
            </Form>
        );
    },
);

// ─── Branch 1: Anon order, brand-new email (no existing account) ──────────────
// Has its own state; accordion + password form are isolated here.
const AnonOrderForNewUser = memo(
    (props: IAnonNewUserProps): React.JSX.Element => {
        // Reqs Branch 1: An anonymous user placed an order using an email address that was
        // entirely new to Oscar.
        const [disableSubmit, setDisableSubmit] = useState(false);
        const [formSubmitted, setFormSubmitted] = useState(false);
        const [passwordField, setPasswordField] = useState("");
        const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
        const [formIsExpanded, setFormIsExpanded] = useState(true);
        const disableSubmitRef = useRef(false);

        const onToggleFormAccordion = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                setFormIsExpanded((prev) => !prev);
            },
            [],
        );

        const onPasswordChange = useCallback(
            (event: React.FormEvent<HTMLInputElement>) => {
                setPasswordField(event.currentTarget.value);
                setPasswordErrors([]);
            },
            [],
        );

        const onSubmitSetNewPassword = useCallback(
            async (event: React.SyntheticEvent<HTMLFormElement>) => {
                event.preventDefault();
                if (disableSubmitRef.current) {
                    return;
                }
                if (passwordField.length <= 0) {
                    setPasswordErrors([
                        t`You must enter a password to create an account`,
                    ]);
                    return;
                }
                disableSubmitRef.current = true;
                setDisableSubmit(true);
                try {
                    await userAPI.changePassword(passwordField);
                    setFormSubmitted(true);
                    setPasswordErrors([]);
                } catch (e) {
                    console.error(e);
                    disableSubmitRef.current = false;
                    setDisableSubmit(false);
                    setPasswordErrors([
                        t`An unexpected error occurred. Please try again.`,
                    ]);
                }
            },
            [passwordField],
        );

        if (formSubmitted) {
            return (
                <>
                    <SVGIcon
                        aria-hidden="true"
                        svgClassName={`${BASE_CLASS}__form-success-icon`}
                        name="checkmark"
                    />
                    <h2 className={`${BASE_CLASS}__title`} role="alert">
                        {t`Success! Your account has been created.`}
                    </h2>
                    <p className={`${BASE_CLASS}__copy`}>
                        {richT(
                            strings.get(
                                "ORDER_THANK_YOU_B1_CREATE_ACCT_SUCCESS",
                            ) ?? "",
                            {
                                MyOrdersLink: (children) => (
                                    <Link
                                        className={`${BASE_CLASS}__link`}
                                        href={props.orderHistoryURL}
                                        target="_top"
                                    >
                                        {children}
                                    </Link>
                                ),
                            },
                        )}
                    </p>
                </>
            );
        }

        const accordionBody = formIsExpanded ? (
            <CSSTransitionWithRef
                key="form"
                classNames="transition--fade"
                timeout={{ exit: 2000, enter: 2000 }}
            >
                <div
                    id="create-expandable-account-form"
                    className={`${BASE_CLASS}__accordion-body`}
                >
                    <p className={`${BASE_CLASS}__copy`}>
                        {strings.get("ORDER_THANK_YOU_B1_CREATE_ACCT_COPY")}
                    </p>
                    <Form
                        className={concatClassNames([
                            FORM_CLASS_NAME,
                            `${BASE_CLASS}__set-password-form`,
                        ])}
                        onSubmit={onSubmitSetNewPassword}
                    >
                        <FormInput
                            id="create_account_email"
                            name="create_account_email"
                            type="email"
                            label={t`Email Address:`}
                            labelPlacement="outside"
                            value={props.email}
                            readOnly={true}
                            disabled={true}
                        />
                        <FormPasswordInput
                            id="create_account_password"
                            name="create_account_password"
                            label={t`Password:`}
                            labelPlacement="outside"
                            value={passwordField}
                            errors={passwordErrors}
                            disabled={disableSubmit}
                            onChange={onPasswordChange}
                        />
                        <FormSubmit
                            className={`button button--full-width ${BASE_CLASS}__submit`}
                            value={t`Create Account`}
                            disabled={disableSubmit}
                        />
                    </Form>
                </div>
            </CSSTransitionWithRef>
        ) : null;

        return (
            <>
                <h2 className={`${BASE_CLASS}__title`}>
                    <button
                        aria-controls="create-expandable-account-form"
                        aria-expanded={formIsExpanded}
                        onClick={onToggleFormAccordion}
                    >
                        {t`Let's create an account`}
                        <SVGIcon
                            aria-hidden="true"
                            svgClassName={`${BASE_CLASS}__accordion-handle`}
                            name={formIsExpanded ? "collapse" : "expand"}
                        />
                    </button>
                </h2>
                <TransitionGroup component={null}>
                    {accordionBody}
                </TransitionGroup>
            </>
        );
    },
);

// ─── Root ─────────────────────────────────────────────────────────────────────
// Stateless router — picks the correct branch based on props.
// All state lives in branch components; this never re-renders due to child state.
// See requirements:
// https://gitlab.com/thelabnyc/tsi-sites/tsi-requirements/blob/feature_9446/tsi-tempurpedic/checkout/order-thank-you.md#create-account-form
export const CreateAccount = memo(
    (props: ICreateAccountProps): React.ReactNode => {
        let content: React.ReactNode;

        if (props.orderOwnerHasPassword) {
            if (props.isAuthenticatedAsOrderOwner) {
                // Branch 4
                content = (
                    <AuthenticatedUserOrder
                        orderHistoryURL={props.orderHistoryURL}
                    />
                );
            } else {
                // Branch 3
                content = (
                    <AnonOrderForRegisteredUser
                        firstName={props.firstName}
                        email={props.email}
                        orderHistoryURL={props.orderHistoryURL}
                    />
                );
            }
        } else if (props.isAuthenticatedAsOrderOwner) {
            // Branch 1
            content = (
                <AnonOrderForNewUser
                    email={props.email}
                    orderHistoryURL={props.orderHistoryURL}
                />
            );
        } else {
            // Branch 2
            content = (
                <AnonOrderForNonRegisteredUser
                    firstName={props.firstName}
                    email={props.email}
                    orderHistoryURL={props.orderHistoryURL}
                />
            );
        }

        return <div className={BASE_CLASS}>{content}</div>;
    },
);
